import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import BankAccountSelector from '../../components/client/BankAccountSelector';
import StepUpCodeField from '../../components/security/StepUpCodeField';
import OnboardingRequiredPanel from '../../components/client/OnboardingRequiredPanel';
import authService from '../../services/auth.service';
import portfolioService from '../../services/portfolio.service';
import { TERM_DATES_UNAVAILABLE, openTermKey, openTermLine } from '../../components/client/openTerms.helpers';
import withdrawalsService from '../../services/withdrawals.service';
import redemptionsService, { type RedemptionRequest } from '../../services/redemptions.service';
import { NavExitSection } from '../../components/client/NavWindowNotice';
import NavWindowDeferred from '../../components/client/NavWindowDeferred';
import NoEarlyExitPanel from '../../components/client/NoEarlyExitPanel';
import { termsOf } from '../../components/client/noEarlyExit.helpers';
import { useClientApprovalGate } from '../../hooks/useCurrentUserProfile';
import { getApiErrorCode, getApiErrorMessage, getRetryAfterSeconds } from '../../lib/apiError';
import { describeMoneyRefusal, type MoneyRefusal } from '../../lib/moneyRefusal';
import RefusalNotice from '../../components/client/RefusalNotice';
import ExecutiveRefusal from '../../components/client/ExecutiveRefusal';
import { CALL_CONTEXT, channelsToExecutive } from '../../lib/executiveRefusal';
import BusinessDayLegend from '../../components/client/BusinessDayLegend';
import emailVerificationService from '../../services/emailVerification.service';
import { formatDate, formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import {
  acknowledgedPenaltyToSend,
  canSubmitWithdrawal,
  hasEarlyWithdrawalCharge,
  RESULT_ON_EXIT_NOTICE,
} from './withdraw.helpers';
import type {
  ProductBalance,
  UpcomingMaturity,
  WithdrawalPenaltyPreview,
  WithdrawalRequest,
  WithdrawalStatus,
} from '../../types/finance';

const PAGE_TITLE = 'Request a withdrawal';
const PAGE_SUBTITLE =
  'Withdraw from one of your holdings to a bank account in your name. Requests are confirmed with a one-time code, then reviewed before the money is released.';

const STATUS_TONE: Record<WithdrawalStatus, BadgeTone> = {
  requested: 'warning',
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
  reversed: 'neutral',
  cancelled: 'neutral',
};

const STATUS_LABEL: Record<WithdrawalStatus, string> = {
  requested: 'Awaiting Account Executive',
  pending: 'In review',
  approved: 'Approved',
  rejected: 'Declined',
  reversed: 'Reversed',
  cancelled: 'Cancelled',
};

export default function Withdraw(): JSX.Element {
  const gate = useClientApprovalGate();
  const [clientId, setClientId] = useState<string | null>(null);
  const [balances, setBalances] = useState<ProductBalance[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  // Requests an exit window paid only in part (a series priced at NAV), and notices of non-renewal
  // still waiting for their day; empty when they could not be read.
  const [redemptions, setRedemptions] = useState<RedemptionRequest[]>([]);
  // The term each contribution is in today; null when it could not be read.
  const [openTerms, setOpenTerms] = useState<UpcomingMaturity[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async (opts: { initial?: boolean } = {}) => {
    // Only the first load blanks the page; a post-submit refresh runs in the
    // background so the form (and its success confirmation) stay mounted.
    if (opts.initial) setLoading(true);
    setError(null);
    try {
      const id = await authService.getMyId();
      const [bal, list, summary, waiting] = await Promise.all([
        portfolioService.getClientBalance(id),
        withdrawalsService.list({ size: 50 }),
        // The dates are context for the decision, never a reason to block the page.
        portfolioService.getClientSummary(id).catch(() => null),
        // What a window deferred, and a notice of non-renewal given, are context too: they never
        // block the page.
        redemptionsService.list({ status: ['partially_filled', 'requested'], size: 50 }).catch(() => null),
      ]);
      setOpenTerms(
        summary && summary.term_dates_available !== false ? summary.upcoming_maturities : null,
      );
      setClientId(id);
      setBalances(bal.items.filter((b) => Number(b.balance) > 0));
      setWithdrawals(list.items);
      setRedemptions(waiting?.items ?? []);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load your holdings.'));
    } finally {
      if (opts.initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (gate !== 'approved') {
      setLoading(false);
      return;
    }
    reload({ initial: true });
  }, [gate, reload]);

  if (gate === 'loading') {
    return (
      <>
        <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      </>
    );
  }

  if (gate === 'pending') {
    return (
      <>
        <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        <OnboardingRequiredPanel feature="Withdrawals" />
      </>
    );
  }

  return (
    <>
      <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}

      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}

      {!loading && clientId && (
        <div className="mb-4 p-3 rounded-lg text-sm"
             style={{ background: colors.warningBg, color: colors.ink }}>
          <p>{RESULT_ON_EXIT_NOTICE}</p>
          {openTerms === null && <p className="mt-2">{TERM_DATES_UNAVAILABLE}</p>}
          {openTerms !== null && openTerms.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {openTerms.map((term) => (
                <li key={openTermKey(term)}>{openTermLine(term)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!loading && clientId && <NavWindowDeferred requests={redemptions} />}

      {!loading && clientId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <WithdrawForm
            clientId={clientId}
            balances={balances}
            openTerms={openTerms}
            redemptions={redemptions}
            onDone={() => reload()}
          />
          <WithdrawalHistory withdrawals={withdrawals} navProductIds={navProductIds(balances)} />
        </div>
      )}
    </>
  );
}

interface FormProps {
  readonly clientId: string;
  readonly balances: ProductBalance[];
  readonly openTerms: UpcomingMaturity[] | null;
  readonly redemptions: readonly RedemptionRequest[];
  readonly onDone: () => void;
}

function WithdrawForm({ clientId, balances, openTerms, redemptions, onDone }: FormProps): JSX.Element {
  const [productId, setProductId] = useState('');
  const [amount, setAmount] = useState('');
  // A series priced at NAV: the client closes the whole holding (S-44), not an amount.
  const [closePosition, setClosePosition] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<MoneyRefusal | null>(null);
  // A refusal that sends the client to their Account Executive, shown with the call (S-43).
  const [executiveRefusal, setExecutiveRefusal] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [preview, setPreview] = useState<WithdrawalPenaltyPreview | null>(null);
  const [quoting, setQuoting] = useState(false);
  // Why the charge could not be priced, if it could not. Blocks submitting.
  const [quoteError, setQuoteError] = useState<string | null>(null);
  // Bumping this re-runs the quote ("Try again").
  const [quoteAttempt, setQuoteAttempt] = useState(0);
  const [chargeAccepted, setChargeAccepted] = useState(false);
  // Set when the money-out gate refuses for an unproven email address, so the
  // error can carry the one action that clears it.
  const [needsEmailVerification, setNeedsEmailVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState<string | null>(null);

  const selected = balances.find((b) => b.product_id === productId);
  // A series held to the end of its term has no withdrawal and no charge to price (owner,
  // 2026-11-01): it is left by a notice of non-renewal instead.
  const heldToTerm = Boolean(selected?.no_early_exit);
  const amountNum = Number(amount);
  const amountValid = !Number.isNaN(amountNum) && amountNum > 0
    && (!selected || amountNum <= Number(selected.balance));
  // Said next to the field and tied to it, so a screen reader hears why it is wrong.
  const amountInvalid = Boolean(selected && amount && !amountValid);

  // Price the withdrawal as the client types, so the exact charge is on screen BEFORE
  // they confirm anything. Debounced, and every earlier answer is discarded when the
  // amount moves on — a stale quote next to a live amount is the one thing worse than
  // no quote. Any acceptance is withdrawn the moment the number it referred to changes.
  useEffect(() => {
    setChargeAccepted(false);
    setQuoteError(null);
    if (!productId || !amountValid || heldToTerm) {
      setPreview(null);
      setQuoting(false);
      return undefined;
    }
    let cancelled = false;
    setQuoting(true);
    const timer = globalThis.setTimeout(() => {
      withdrawalsService.penaltyPreview(productId, amount)
        .then((quote) => { if (!cancelled) setPreview(quote); })
        .catch((err: unknown) => {
          // Was silent: the charge section simply did not render and submitting
          // stayed enabled, so a client could send a request for a charge they
          // had never been shown.
          if (!cancelled) {
            setPreview(null);
            setQuoteError(getApiErrorMessage(
              err,
              'We could not calculate the early-withdrawal charge right now.',
            ));
          }
        })
        .finally(() => { if (!cancelled) setQuoting(false); });
    }, 400);
    return () => { cancelled = true; globalThis.clearTimeout(timer); };
  }, [productId, amount, amountValid, heldToTerm, quoteAttempt]);

  const formValid = canSubmitWithdrawal({
    productId,
    accountId,
    amount,
    availableBalance: selected?.balance ?? '',
    mfaCode,
    preview,
    chargeAccepted,
    quoting,
    quoteFailed: quoteError !== null,
  });
  const charged = hasEarlyWithdrawalCharge(preview);

  // What the client is told when the request is refused: each refusal gets its own next step.
  const showSubmitError = (e: unknown): void => {
    const code = getApiErrorCode(e);
    const retryAfter = getRetryAfterSeconds(e);
    // A hold, an unsupported country or a destination that cannot receive money
    // are the refusals with no one-step fix; they get the state written for them
    // rather than the server's sentence in a red box.
    const designed = describeMoneyRefusal(e);
    if (designed !== null) {
      setRefusal(designed);
    } else if (code === 'email_not_verified') {
      // The money-out gate (migration 0068). The client can clear it in one
      // step, so the message must point at that step — a blocked withdrawal
      // with no stated way forward is the failure mode this branch exists to
      // avoid.
      setNeedsEmailVerification(true);
      setError('Please confirm your email address before moving money out.');
    } else if (retryAfter !== null) {
      // Without this the user was told "could not submit" and retried
      // immediately, deepening the limit.
      setError(
        `Too many attempts. Please wait about ${retryAfter} second${retryAfter === 1 ? '' : 's'} and try again.`,
      );
    } else if (code === 'mfa_not_enrolled') {
      setError('Turn on two-factor authentication in your profile security settings before requesting a withdrawal.');
    } else if (code === 'penalty_acknowledgement_required' || code === 'penalty_acknowledgement_mismatch') {
      // The charge moved between the quote on screen and this submit. Re-price and
      // make the client accept the new figure — never send the old one again.
      setChargeAccepted(false);
      setError('The early withdrawal charge has changed. Review the updated amount and confirm it to continue.');
      withdrawalsService.penaltyPreview(productId, amount)
        .then((quote) => setPreview(quote))
        .catch((quoteErr: unknown) => {
          setPreview(null);
          setQuoteError(getApiErrorMessage(
            quoteErr,
            'We could not calculate the early-withdrawal charge right now.',
          ));
        });
    } else if (channelsToExecutive(code)) {
      setExecutiveRefusal(getApiErrorMessage(e, 'Could not submit your request.'));
    } else {
      setError(getApiErrorMessage(e, 'Could not submit your request.'));
    }
  };

  const submit = async (): Promise<void> => {
    if (!formValid || !selected || !accountId) return;
    setBusy(true);
    setError(null);
    setRefusal(null);
    setExecutiveRefusal(null);
    try {
      const acknowledged = acknowledgedPenaltyToSend({ preview, chargeAccepted });
      await withdrawalsService.request({
        product_id: productId,
        amount,
        currency_code: selected.currency_code,
        client_bank_account_id: accountId,
        client_reason: reason.trim() || null,
        ...(closePosition ? { close_position: true } : {}),
        ...(acknowledged === undefined
          ? {}
          : {
              acknowledged_penalty_amount: acknowledged,
              ...(preview?.acceptance_sha256
                ? { acknowledged_text_sha256: preview.acceptance_sha256 }
                : {}),
            }),
        mfa_code: mfaCode.trim(),
      });
      setFlash('Withdrawal requested. Your Account Executive will reach out before it is released.');
      setProductId(''); setAmount(''); setClosePosition(false); setReason(''); setMfaCode('');
      setPreview(null); setChargeAccepted(false);
      globalThis.setTimeout(() => setFlash(null), 8000);
      onDone();
    } catch (e: unknown) {
      showSubmitError(e);
    } finally {
      setBusy(false);
    }
  };

  if (balances.length === 0) {
    return (
      <div className="rounded-2xl p-6 text-sm"
           style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
        You have no holdings with an available balance to withdraw yet.
      </div>
    );
  }

  return (
    <div className="rounded-2xl p-5"
         style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      {flash && (
        <>
          <output className="block mb-3 p-3 rounded-lg text-sm"
               style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.successText }}>
            {flash}
          </output>
          {/* The request is IN. What follows is the one question a client asks
              next — when does the money actually move — answered before they
              have to ask it, and with the reassurance that nothing is waiting
              on them. The platform counts Monday to Friday and keeps no
              bank-holiday calendar, so this is where that is said. */}
          <div className="mb-4">
            <BusinessDayLegend tone="scheduled" />
          </div>
        </>
      )}
      {refusal && <RefusalNotice refusal={refusal} />}
      {executiveRefusal && (
        <ExecutiveRefusal message={executiveRefusal} productId={productId || null} context={CALL_CONTEXT.withdrawal} />
      )}
      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          <p className="m-0">{error}</p>
          {needsEmailVerification && !verificationSent && (
            <button
              type="button"
              className="mt-2 underline"
              style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer' }}
              onClick={() => {
                void emailVerificationService
                  .requestLink()
                  .then(() => setVerificationSent('Check your inbox — the link expires shortly.'))
                  .catch((err: unknown) =>
                    setVerificationSent(
                      getApiErrorMessage(err, 'We could not send the link. Please try again shortly.'),
                    ),
                  );
              }}
            >
              Send me a confirmation link
            </button>
          )}
          {verificationSent && <p className="m-0 mt-2">{verificationSent}</p>}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="wd-product" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
            From holding
          </label>
          <select
            id="wd-product"
            value={productId}
            onChange={(e) => { setProductId(e.target.value); setClosePosition(false); }}
            disabled={busy}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.controlBorder}`, background: colors.surface }}
          >
            <option value="">— Select a holding —</option>
            {balances.map((b) => (
              <option key={b.product_id} value={b.product_id}>
                {b.product_name} — {formatMoney(b.balance, b.currency_code)} available
              </option>
            ))}
          </select>
        </div>

        <HoldingExit holding={selected} terms={openTerms} requests={redemptions} onDone={onDone}>
          <div>
            <label htmlFor="wd-amount" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
              Amount{selected ? ` (${selected.currency_code})` : ''}
            </label>
            <input
              id="wd-amount"
              type="number"
              step="0.01"
              inputMode="decimal"
              min="0"
              value={amount}
              onChange={(e) => { setAmount(limitToCents(e.target.value)); setClosePosition(false); }}
              disabled={busy || !selected}
              placeholder="0.00"
              aria-invalid={amountInvalid}
              aria-describedby={amountInvalid ? 'wd-amount-error' : undefined}
              className="w-full px-3 py-2 rounded-lg text-sm font-mono"
              style={{ border: `1px solid ${amountInvalid ? colors.danger : colors.controlBorder}` }}
            />
            {amountInvalid && selected && (
              <p id="wd-amount-error" className="text-xs mt-1" style={{ color: colors.danger }}>
                Enter an amount up to {formatMoney(selected.balance, selected.currency_code)}.
              </p>
            )}
            <NavExitSection
              selected={selected}
              preview={preview}
              closing={closePosition}
              onCloseAll={(whole) => { setAmount(whole); setClosePosition(true); }}
            />
          </div>

          <fieldset>
            <legend className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
              Destination account
            </legend>
            <BankAccountSelector
              clientId={clientId}
              onSelectionChange={(id) => setAccountId(id)}
            />
          </fieldset>

          <div>
            <label htmlFor="wd-reason" className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
              Reason (optional)
            </label>
            <textarea
              id="wd-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              rows={2}
              maxLength={2000}
              placeholder="Helps your Account Executive understand your needs."
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.controlBorder}` }}
            />
          </div>

          <QuoteUnavailable message={quoteError} onRetry={() => setQuoteAttempt((n) => n + 1)} />
          {charged && preview && (
            <div
              className="rounded-lg p-4"
              style={{ background: colors.warningBg, border: `1px solid ${colors.warning}` }}
            >
              <p className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>
                Withdrawing early carries a charge
              </p>
              <dl className="space-y-1 text-sm" style={{ color: colors.ink }}>
                <div className="flex justify-between">
                  {/* At NAV the window's NAV decides the figures: today's are an estimate (S-56). */}
                  {preview.exit_window_at ? (
                    <dt style={{ color: colors.inkSoft }}>Early withdrawal charge (estimate)</dt>
                  ) : (
                    <dt style={{ color: colors.inkSoft }}>Early withdrawal charge</dt>
                  )}
                  <dd className="font-mono font-semibold">
                    {formatMoney(preview.penalty_amount, preview.currency_code)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  {preview.exit_window_at ? (
                    <dt style={{ color: colors.inkSoft }}>You receive (estimate)</dt>
                  ) : (
                    <dt style={{ color: colors.inkSoft }}>You receive</dt>
                  )}
                  <dd className="font-mono font-semibold">
                    {formatMoney(preview.net_amount, preview.currency_code)}
                  </dd>
                </div>
              </dl>
              <label className="flex items-start gap-2 mt-3 text-sm cursor-pointer" style={{ color: colors.ink }}>
                <input
                  type="checkbox"
                  checked={chargeAccepted}
                  disabled={busy}
                  onChange={(e) => setChargeAccepted(e.target.checked)}
                  className="mt-0.5"
                />
                {/* The sentence is the server's, in the reader's language: what is accepted is
                    what was stored (M-8). */}
                <span>{preview.acceptance_text}</span>
              </label>
            </div>
          )}

          <StepUpCodeField
            id="wd-mfa"
            value={mfaCode}
            onChange={setMfaCode}
            hint="From your authenticator app."
          />
        </HoldingExit>
      </div>

      {/* Before they commit, not after: when the money actually moves is part of
          what they are agreeing to, and a client who learns it from the
          confirmation screen learned it too late to plan around. */}
      <WithdrawalOnly holding={selected}>
        <div className="mt-6">
          <BusinessDayLegend tone="standing" />
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={submit}
            disabled={busy || !formValid}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: busy || !formValid ? colors.disabledBg : gradients.gold,
              color: busy || !formValid ? colors.disabledText : colors.navy,
            }}
          >
            {busy ? 'Submitting…' : 'Request withdrawal'}
          </button>
        </div>
      </WithdrawalOnly>
    </div>
  );
}

/** A series held to the end of its term (owner, 2026-11-01) is left by a notice of non-renewal, never
 *  withdrawn: its panel stands where the withdrawal fields (`children`) are for any other holding. */
function HoldingExit({
  holding,
  terms,
  requests,
  onDone,
  children,
}: Readonly<{
  holding: ProductBalance | undefined;
  terms: UpcomingMaturity[] | null;
  requests: readonly RedemptionRequest[];
  onDone: () => void;
  children: ReactNode;
}>): JSX.Element {
  if (!holding?.no_early_exit) return <>{children}</>;
  return (
    <NoEarlyExitPanel
      key={holding.product_id}
      holding={holding}
      terms={termsOf(holding.product_id, terms)}
      requests={requests}
      onDone={onDone}
    />
  );
}

/** What only a withdrawal shows (when the money moves, the submit): nothing for a series held to term. */
function WithdrawalOnly({
  holding,
  children,
}: Readonly<{ holding: ProductBalance | undefined; children: ReactNode }>): JSX.Element | null {
  return holding?.no_early_exit ? null : <>{children}</>;
}

/** The products the client holds in a series priced at NAV (FI-17 values their holding): their requests wait for a window. */
function navProductIds(balances: readonly ProductBalance[]): Set<string> {
  return new Set(balances.filter((b) => b.nav).map((b) => b.product_id));
}

const WAITING_STATUSES: ReadonlySet<WithdrawalStatus> = new Set(['requested', 'pending', 'approved']);

/** "Waiting for the window of <date>" for a request on a series priced at NAV that is not paid yet; null otherwise. */
function windowLine(w: WithdrawalRequest, navIds: ReadonlySet<string>): string | null {
  if (!w.funds_eligible_at || !navIds.has(w.product_id) || !WAITING_STATUSES.has(w.status)) return null;
  return `Waiting for the window of ${formatDate(w.funds_eligible_at)}, paid at that window's approved NAV.`;
}

function WithdrawalHistory({
  withdrawals,
  navProductIds: navIds,
}: Readonly<{ withdrawals: WithdrawalRequest[]; navProductIds: ReadonlySet<string> }>): JSX.Element {
  return (
    <section>
      <h2 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: colors.inkSoft }}>
        Your withdrawals
      </h2>
      {withdrawals.length === 0 ? (
        <div className="rounded-2xl p-6 text-sm"
             style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}>
          You have not requested any withdrawals yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {withdrawals.map((w) => (
            <li key={w.id}
                className="rounded-xl p-4 flex items-start justify-between gap-3"
                style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
              <div className="min-w-0">
                <div className="font-medium text-sm" style={{ color: colors.ink }}>{w.product_name}</div>
                <div className="text-xs mt-0.5" style={{ color: colors.mutedSoft }}>
                  {formatDateTime(w.created_at)}{w.reference_number ? ` · ${w.reference_number}` : ''}
                </div>
                {/* Written for the client by whoever declined it. */}
                {windowLine(w, navIds) && (
                  <div className="text-xs mt-1" style={{ color: colors.inkSoft }}>{windowLine(w, navIds)}</div>
                )}
                {w.status === 'rejected' && w.rejection_reason && (
                  <div className="text-xs mt-1" style={{ color: colors.inkSoft }}>
                    {w.rejection_reason}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <span className="font-mono text-sm font-semibold" style={{ color: colors.inkSoft }}>
                  {formatMoney(w.amount, w.currency_code)}
                </span>
                <StatusBadge tone={STATUS_TONE[w.status]}>{STATUS_LABEL[w.status]}</StatusBadge>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Shown when the early-withdrawal charge could not be priced. Submitting stays
 *  disabled meanwhile: a charge is never accepted without being seen, and the
 *  request itself re-prices through the same path, so it would fail anyway. */
function QuoteUnavailable({
  message,
  onRetry,
}: Readonly<{ message: string | null; onRetry: () => void }>): JSX.Element | null {
  if (!message) return null;
  return (
    <div
      className="rounded-lg p-3 text-sm"
      role="alert"
      style={{ background: colors.warningBg, border: `1px solid ${colors.warning}`, color: colors.ink }}
    >
      <p className="m-0">{message} You can submit once the charge is shown.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 underline"
        style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer' }}
      >
        Try again
      </button>
    </div>
  );
}
